import React, { useEffect, useState } from 'react';
import { Input } from 'reactstrap';
import { Search48 } from '@bphxd/ds-core-react/icons';
import { useReactTable, getCoreRowModel, flexRender, getPaginationRowModel, getFilteredRowModel } from '@tanstack/react-table';
import PropTypes from 'prop-types';
import './_commonReactTable.scss';

const CommonReactTable = ({ columns, data, pagination }) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: pagination?.pageSize || 50,
  });
  const pageSizeOptions = [10, 20, 50, 100];

  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPaginationState(prev => ({
      ...prev,
      pageSize: newPageSize,
      pageIndex: 0, // Reset to first page when changing page size
    }));
  };

  const table = useReactTable({
    data,  // Ensure data is passed directly from parent
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPaginationState,
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
      pagination: paginationState,
    },
    manualPagination: false, // Client-side pagination
  });

  // Handle prev page
  const handlePrevClick = (e) => {
    if (table.getCanPreviousPage()) {
      table.previousPage();
    }
    e.preventDefault(); // Prevent default action to stop event bubbling
  };

  // Handle next page
  const handleNextClick = (e) => {
    if (table.getCanNextPage()) {
      table.nextPage();
    }
    e.preventDefault(); // Prevent default action to stop event bubbling
  };

  return (
    <>
      <div className="offset-lg-9">
        <div className="d-inline-block">
          <div className="input-group">
            <Input
              type="text"
              data-testid="table-field-search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search..."
            />
            <div className="input-group-append">
              <div style={{
                height: '46px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0',
                border: '1px solid #dedede',
              }}>
                <div className="d-flex align-items-center justify-content-center">
                  <Search48 className="search-icon" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <table className="table commonReactTable">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className={header.column.id === 'actions' ? 'text-center' : ''}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination-wrapper">
        <div className="pagination-container d-flex justify-content-between align-items-center mt-3">
          {/* Page Size Selector (Left) */}
          <div className="page-size-selector">
            <label htmlFor="page-size" className="me-2">Records per page:</label>
            <select
              id="page-size"
              value={paginationState.pageSize}
              onChange={handlePageSizeChange}
              className="form-select"
              style={{ width: 'auto', display: 'inline-block' }}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Pagination Controls (Center) */}
          <div className="pagination-nav-left d-flex">
            <div
              onClick={handlePrevClick}
              className={`pagination-item ${!table.getCanPreviousPage() ? 'disabled' : ''}`}
            >
              Prev
            </div>

            <div
              onClick={handleNextClick}
              className={`pagination-item ${!table.getCanNextPage() ? 'disabled' : ''}`}
            >
              Next
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

CommonReactTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  pagination: PropTypes.shape({
    pageIndex: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    onNext: PropTypes.func.isRequired,
    onPrevious: PropTypes.func.isRequired,
    onPageSizeChange: PropTypes.func,
    canPrev: PropTypes.bool.isRequired,
    canNext: PropTypes.bool.isRequired,
  }).isRequired,
};

export default CommonReactTable;
